import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Mail, MailOpen } from "lucide-react";

export default function Inbox() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [active, setActive] = useState(null);

  const load = () =>
    api
      .get("/inbox")
      .then((r) => setMessages(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const openMsg = async (m) => {
    setActive(m);
    if (!m.read) {
      await api.patch(`/inbox/${m.id}/read`).catch(() => {});
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    }
  };

  return (
    <div className="space-y-8" data-testid="inbox-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("inbox")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Platform Inbox</h1>
        <div className="font-mono text-[#0033FF] mt-2">{user?.platform_email}</div>
      </div>

      <div className="grid md:grid-cols-12 gap-0 border border-black/10 min-h-[500px]">
        <div className="md:col-span-5 border-e border-black/10 max-h-[70vh] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">No messages yet.</div>
          ) : (
            messages.map((m) => (
              <button
                key={m.id}
                onClick={() => openMsg(m)}
                className={`w-full text-start block border-b border-black/10 p-4 hover:bg-gray-50 ${
                  active?.id === m.id ? "bg-[#F8F9FA]" : ""
                } ${!m.read ? "bg-white" : "bg-white/60"}`}
                data-testid={`msg-${m.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {m.read ? <MailOpen className="w-3.5 h-3.5 text-gray-400" /> : <Mail className="w-3.5 h-3.5 text-[#FF3B30]" />}
                  <span className="font-display font-bold text-sm truncate">{m.from_name}</span>
                </div>
                <div className={`text-sm ${m.read ? "text-gray-500" : "text-black font-medium"} truncate`}>{m.subject}</div>
                <div className="font-mono text-[10px] text-gray-400 uppercase mt-1">
                  {new Date(m.received_at).toLocaleString()}
                </div>
              </button>
            ))
          )}
        </div>
        <div className="md:col-span-7 p-6 md:p-10">
          {active ? (
            <div>
              <h2 className="font-display font-bold text-2xl mb-2">{active.subject}</h2>
              <div className="text-sm text-gray-500 mb-6 font-mono">
                from {active.from_name} &lt;{active.from_email}&gt;
              </div>
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed" data-testid="msg-body">{active.body}</div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Select a message</div>
          )}
        </div>
      </div>
    </div>
  );
}
